import { normalizePrice } from './format'
import { priceStateOf, type PriceState } from './vehiclePricing'
import type { WidgetBootstrapResponse } from '../types/PublicBookingChannelTypes'

/**
 * The opening screen asks "what does your car need?" rather than "what's your
 * registration?". The answers are derived from the channel's own service
 * categories — never a hardcoded list of job types, because a garage that
 * doesn't fit tyres must not be offered a Tyres tile.
 */

export type JobIconName =
  | 'servicing'
  | 'mot'
  | 'brakes'
  | 'tyres'
  | 'aircon'
  | 'diagnostics'
  | 'battery'
  | 'exhaust'
  | 'clutch'
  | 'suspension'
  | 'windscreen'
  | 'wheels'
  | 'repairs'
  | 'other'

/**
 * Category name to icon.
 *
 * Ordered most specific first: "brake fluid service" is a brake job, not a
 * service, so brakes are tested before servicing.
 *
 * Anchored on word boundaries rather than plain substrings — a garage
 * category of "Motability Paperwork" contains "mot" and would otherwise be
 * given an MOT icon. Stems like `servic` deliberately match forward
 * (servicing, services) but still have to start a word.
 */
const ICON_PATTERNS: Array<[JobIconName, RegExp]> = [
  ['mot', /\bm\.?o\.?t\b/],
  ['brakes', /\bbrake|\bdisc\b|\bpads?\b/],
  ['tyres', /\btyre|\btire|\bpuncture/],
  ['wheels', /\bwheel|\balign|\btracking\b|\bbalanc|\bgeometr/],
  ['aircon', /\bair[-\s]?con|\ba\/c\b|\bclimate\b|\bre-?gas/],
  ['battery', /\bbatter|\bcharging\b|\balternator|\bstarter\b/],
  ['exhaust', /\bexhaust|\bemission|\bdpf\b|\bcatalytic/],
  ['clutch', /\bclutch|\bgearbox|\btransmission/],
  ['suspension', /\bsuspension|\bshock|\bspring|\bsteering/],
  ['windscreen', /\bwindscreen|\bwindshield|\bglass\b|\bwiper|\bchip\b/],
  ['diagnostics', /\bdiagnos|\bfault|\bwarning light|\bengine light|\bscan\b/],
  ['servicing', /\bservic|\boil\b|\binterim\b|\bmajor\b|\bmaintenance/],
  ['repairs', /\brepair|\bfix\b|\breplace|\bfitting\b/],
]

/** Best-guess icon for a category name. Falls back to a generic spanner. */
export function matchJobIcon(category: string): JobIconName {
  const value = category.trim().toLowerCase()
  if (!value) return 'other'

  for (const [icon, pattern] of ICON_PATTERNS) {
    if (pattern.test(value)) return icon
  }

  return 'other'
}

/**
 * True when a service is an MOT test.
 *
 * The category alone is not enough: the portal files every widget service under
 * "Services", so at most garages no category says MOT and the expired-MOT prompt
 * never showed (ACA-164). The server's own judgement comes first; the category
 * is the fallback for a backend too old to send it.
 */
export function isMotService(service: WidgetBootstrapResponse.ServiceItem): boolean {
  return service.motService === true || matchJobIcon(service.category ?? '') === 'mot'
}

/** Where a garage's MOT sits in the list of job types. */
export interface MotOffer {
  jobType: JobType
  /**
   * The MOT itself when it is one service inside a wider category ("Services"),
   * so adding it adds that service, not whatever the category leads with. Null
   * when the MOT has a category of its own, which then behaves as its card does.
   */
  service: WidgetBootstrapResponse.ServiceItem | null
}

/** The garage's MOT, or null when it does not sell one online. A retest never counts. */
export function findMot(jobTypes: JobType[]): MotOffer | null {
  const own = jobTypes.find((jobType) => matchJobIcon(jobType.key) === 'mot')
  if (own) return { jobType: own, service: null }

  for (const jobType of jobTypes) {
    const service = jobType.bookable.find(
      (candidate) => isMotService(candidate) && !/\bre-?test\b/i.test(candidate.name ?? '')
    )
    if (service) return { jobType, service }
  }
  return null
}

export interface JobType {
  /** Stable key: the lower-cased category. */
  key: string
  label: string
  icon: JobIconName
  services: WidgetBootstrapResponse.ServiceItem[]
  /** Cheapest priced service in the category, or null when none are priced. */
  /**
   * The services a customer can actually choose between, with conditional ones
   * like an MOT retest left out. What the flow counts when deciding whether this
   * category is a question at all.
   */
  bookable: WidgetBootstrapResponse.ServiceItem[]
  /** Cheapest service you pay for, or null when the category is free or unpriced. */
  fromPrice: number | null
  /**
   * True when the service this category exists for carries no price, so any
   * "from" figure would be quoting a different job.
   */
  quoteOnly: boolean
  /** True when every service in the category costs the same, so "from" is misleading. */
  singlePrice: boolean
  /**
   * ACA-96: why the headline service has no price, when it depends on the car:
   * 'afterReg' before the lookup, 'onRequest' when the garage confirms it.
   */
  headlinePriceState: PriceState
  /**
   * Ready-to-show label for a free service in this category, e.g. "Free brake
   * check". Null when there isn't one.
   *
   * Kept separate from the price so a category can say both — "from £59" and
   * "Free brake check" are each true of the same garage, and a free check is
   * the strongest reason to tap the tile.
   */
  freeOption: string | null
}

/**
 * How to advertise a free service, using the garage's own wording.
 *
 * Garages usually name these "Free brake check" already, so prefixing blindly
 * would produce "Free Free brake check". The name is otherwise left exactly as
 * written — lower-casing the first letter to make it read as a phrase turns
 * "MOT retest" into "mOT retest".
 */
function describeFreeOption(services: WidgetBootstrapResponse.ServiceItem[]): string | null {
  const free = services.find((service) => normalizePrice(service.price) === 0)
  if (!free) return null

  const name = free.name?.trim()
  if (!name) return null

  return /^free\b/i.test(name) ? name : `Free ${name}`
}

function sortServices(
  services: WidgetBootstrapResponse.ServiceItem[]
): WidgetBootstrapResponse.ServiceItem[] {
  return [...services].sort((a, b) =>
    a.displayOrder !== b.displayOrder ? a.displayOrder - b.displayOrder : a.name.localeCompare(b.name)
  )
}

/**
 * Groups the channel's services into pickable job types.
 *
 * Preserves the order the garage configured — the first category they listed
 * is the first tile — rather than imposing our own idea of what matters.
 */
export function buildJobTypes(services: WidgetBootstrapResponse.ServiceItem[]): JobType[] {
  const order: string[] = []
  const labels = new Map<string, string>()
  const grouped = new Map<string, WidgetBootstrapResponse.ServiceItem[]>()

  for (const service of services) {
    const label = (service.category ?? 'Other').trim().replace(/\s+/g, ' ') || 'Other'
    const key = label.toLowerCase()

    if (!grouped.has(key)) {
      grouped.set(key, [])
      labels.set(key, label)
      order.push(key)
    }
    grouped.get(key)?.push(service)
  }

  return order.map((key) => {
    const items = sortServices(grouped.get(key) ?? [])

    // The headline quote is the cheapest thing you actually pay for. A free
    // service used to count, which read fine for a free brake check and lied
    // outright for MOT: the free retest made the tile say "Free check" over a
    // £54.85 test.
    const paidPrices = items
      .map((service) => normalizePrice(service.price))
      .filter((price): price is number => price != null && price > 0)

    const bookable = items.filter((service) => !isConditional(service))

    // The headline price has to be the price of the headline thing. Tyre supply
    // cannot be priced online yet, so the Tyres category holds an unpriced fitting
    // and a £25 puncture repair — and the tile read "Tyres from £25", which is the
    // price of a puncture repair and nothing to do with a tyre.
    //
    // A category therefore only quotes a "from" price when the service it exists
    // for has one. Where it does not, the tile says so rather than borrowing a
    // number from a different job.
    // The headline is what the garage put first, unless they nominated one — the
    // same rule the tyre channel already uses to decide which service a category
    // is really for.
    const headline = nominatedPrimary(bookable) ?? bookable[0] ?? null
    const quoteOnly = headline != null && normalizePrice(headline.price) == null

    const fromPrice = !quoteOnly && paidPrices.length > 0 ? Math.min(...paidPrices) : null
    const singlePrice =
      paidPrices.length > 0 && Math.min(...paidPrices) === Math.max(...paidPrices)

    return {
      key,
      label: labels.get(key) ?? 'Other',
      icon: matchJobIcon(key),
      services: items,
      bookable,
      fromPrice,
      quoteOnly,
      // "from £54.85" reads oddly when there is only one price to quote. Keyed on
      // how many things are priced, not how many rows exist: a category holding a
      // £54.85 test and a free retest quotes one price, not a range.
      singlePrice,
      headlinePriceState: priceStateOf(headline),
      freeOption: describeFreeOption(items),
    }
  })
}

/**
 * Work a customer cannot choose to have — only to end up needing.
 *
 * An MOT retest is the case that matters: it is free, it is real, and it exists
 * only after failing an MOT at that garage. Listed as an ordinary option it turns
 * "MOT" from one tap into a question nobody browsing can answer, which is exactly
 * what a customer means when they say they have already picked MOT.
 *
 * The garage's own answer wins: presentationRole is set per service in the
 * booking widget settings. The heuristic below only covers channels configured
 * before that existed, and is deliberately the narrowest rule that solves the
 * case — free AND named as a retest. Nothing broader: "Free brake check" is also
 * £0 and is a genuine reason to book.
 */
function isConditional(service: WidgetBootstrapResponse.ServiceItem): boolean {
  if (service.presentationRole) return service.presentationRole === 'conditional'

  if (normalizePrice(service.price) !== 0) return false
  return /\bre-?test\b/i.test(service.name ?? '')
}

/**
 * The service a garage has nominated as what its category is for, if any.
 *
 * Only a single nomination counts. A category with Interim and Full Service both
 * marked primary has not said which one Servicing means, and picking the first
 * put the cheaper service in the basket of everyone who wanted the other (ACA-100).
 */
function nominatedPrimary(
  services: WidgetBootstrapResponse.ServiceItem[]
): WidgetBootstrapResponse.ServiceItem | null {
  const primaries = services.filter((service) => service.presentationRole === 'primary')
  return primaries.length === 1 ? primaries[0] : null
}

/**
 * True when picking this tile is the whole answer — one thing to book, nothing to
 * choose. The flow then skips straight past service selection.
 *
 * Counts bookable services only. A category holding an MOT test and a free retest
 * offers exactly one decision, and asking about it reads as being asked twice.
 */
export function isSingleServiceJob(jobType: JobType): boolean {
  return primaryService(jobType) !== null
}

/**
 * The service to add when a category is picked with no choice to make. Null when
 * the customer genuinely has to decide.
 *
 * A garage that nominated a primary service gets that, however many other
 * options sit beside it: they have said "this is what Servicing means here", and
 * anything else stays available behind the card.
 */
export function primaryService(
  jobType: JobType
): WidgetBootstrapResponse.ServiceItem | null {
  const nominated = nominatedPrimary(jobType.bookable)
  if (nominated) return nominated

  return jobType.bookable.length === 1 ? jobType.bookable[0] : null
}

/**
 * True when the category's card opens to show a list: more than one thing to
 * choose between, or a conditional service (an MOT retest) that has to stay
 * reachable without being added for everyone.
 */
export function hasOptionsPanel(jobType: JobType): boolean {
  return jobType.bookable.length > 1 || jobType.services.length > jobType.bookable.length
}

export function findJobType(jobTypes: JobType[], key: string | null): JobType | null {
  if (!key) return null
  return jobTypes.find((jobType) => jobType.key === key) ?? null
}
